/**
 * SCOPE-AI REFINE PROPOSALS — what the page may claim about one.
 *
 * The backend's refine-item route no longer changes anything: it returns a
 * PROPOSED refinement for a human to review. This module is the only place
 * the page learns that, and it fails closed:
 *
 *   A PAYLOAD THAT DOES NOT PROVE IT IS AN UNAPPLIED PROPOSAL IS NOT
 *   RENDERED AS ONE. AND NOTHING HERE EVER SAYS "UPDATED".
 *
 * Everything is pure. It takes what the backend sent and decides what may be
 * claimed from it; it changes no state and triggers no request.
 */

const isNonEmptyObject = (v) =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0;

const REFUSED = Object.freeze({ readable: false, proposal: null });

/**
 * Read the refine-item response. `readable: false` means the payload could
 * not prove it is an unapplied proposal — including a payload that claims it
 * WAS applied, which this page has no authority to believe.
 */
export const readRefineItemProposal = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ...REFUSED, reason: 'The refinement response was not readable.' };
  }
  if (payload.proposal_state !== 'PROPOSED'
    || payload.requires_human_decision !== true
    || payload.applied !== false) {
    return {
      ...REFUSED,
      reason: 'The response did not prove it is an unapplied proposal.',
    };
  }
  if (!Array.isArray(payload.proposals) || payload.proposals.length === 0) {
    return { ...REFUSED, reason: 'The response carried no reviewable proposals.' };
  }
  const items = [];
  for (const raw of payload.proposals) {
    const fingerprint = raw?.current_item_basis?.fingerprint;
    if (
      !raw || typeof raw !== 'object'
      || !raw.item_name
      || !isNonEmptyObject(raw.current_item)
      || !isNonEmptyObject(raw.proposed_item)
      || typeof fingerprint !== 'string' || fingerprint.length !== 64
    ) {
      return { ...REFUSED, reason: 'A proposal in the response could not be verified.' };
    }
    items.push({
      itemName: raw.item_name,
      current: raw.current_item,
      proposed: raw.proposed_item,
      summary: raw.summary_of_changes || '',
      fingerprint,
    });
  }
  return {
    readable: true,
    reason: null,
    proposal: {
      notApplied: true,
      items,
      warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
      itemsWithoutMatch: Array.isArray(payload.items_without_match)
        ? payload.items_without_match
        : [],
    },
  };
};

/**
 * What the page does after a readable proposal. A DECISION, not a rendering
 * detail: activities are NOT refreshed (nothing changed), feedback is NOT
 * cleared (nothing acted on it), and the toast says review, never updated.
 */
export const afterRefineProposal = () => ({
  refreshActivities: false,
  clearFeedback: false,
  toast: 'Refinement proposal prepared for review — nothing has been applied.',
});

/** The badge every proposal card must carry. One phrase, learned once. */
export const NOT_APPLIED_LABEL = 'PROPOSED — NOT APPLIED';
